import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface Review {
  author: string;
  rating: number;
  title: string;
  text: string;
  date: string;
  verified: boolean;
  url?: string;
}

// Smart sort: first 3 are latest by date, rest sorted by text length (most detailed)
const smartSortReviews = (reviews: Review[]): Review[] => {
  if (reviews.length <= 3) return reviews;
  
  const sortedByDate = [...reviews].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  
  const latestThree = sortedByDate.slice(0, 3);
  const remaining = sortedByDate.slice(3).sort(
    (a, b) => b.text.length - a.text.length
  );
  
  return [...latestThree, ...remaining];
};

interface TrustpilotStats {
  reviewCount: number | null;
  rating: number | null;
  reviews: Review[];
  loading: boolean;
}

// Helper to format relative time with smart unit selection
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks === 1) return '1 week ago';
  if (diffWeeks < 4) return `${diffWeeks} weeks ago`;
  if (diffMonths === 1) return '1 month ago';
  if (diffMonths < 12) return `${diffMonths} months ago`;
  
  return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
};

const generateFallbackReviews = (): Review[] => {
  return [
    { author: "Julie", rating: 5, title: "Smooth and easy", text: "Smooth and easy! I wasn't suitable for one but the lady on the phone helped me with a different one instead.", date: "2026-03-25T12:00:00.000Z", verified: true },
    { author: "Grace Lawrence", rating: 5, title: "Quick", text: "Quick, easy to use and friendly staff. Would recommend to anyone.", date: "2026-03-24T12:00:00.000Z", verified: true },
    { author: "Louise", rating: 5, title: "Very convenient", text: "Very convenient, helpful and quick.", date: "2026-03-20T12:00:00.000Z", verified: true },
    { author: "Emma Croucher", rating: 5, title: "Cannot fault at all", text: "Cannot fault at all. Rang me really fast and my medicine was with me 48 hours later. Really pleased with the service.", date: "2026-03-15T12:00:00.000Z", verified: true },
    { author: "Shannon", rating: 5, title: "Friendly and speedy service!", text: "Was referred by a friend as I didn't realise this service existed and I was paying £60+ a year for my prescription! Great service from start to finish. Katie was really friendly over the phone.", date: "2026-03-10T12:00:00.000Z", verified: true },
    { author: "Sophie Smith", rating: 5, title: "Brilliant!!!", text: "Great service. The consultation is super quick and quick delivery after that. I can't believe that I hadn't heard of this service before - what a game changer!", date: "2026-03-05T12:00:00.000Z", verified: true },
    { author: "Maria", rating: 5, title: "10/10", text: "Free consultation. Quick delivery. All concerns heard.", date: "2026-02-28T12:00:00.000Z", verified: true },
    { author: "Kirsty Jonno", rating: 5, title: "Quick and really good", text: "Quick and they are really good at being contacted after my work day.", date: "2026-02-20T12:00:00.000Z", verified: true },
    { author: "Chengetai", rating: 5, title: "Excellent service", text: "Excellent service.", date: "2026-02-15T12:00:00.000Z", verified: true },
    { author: "customer", rating: 5, title: "Really great service", text: "Really great service, so simple and easy! Much easier than having to go to a pharmacy each time.", date: "2026-02-10T12:00:00.000Z", verified: true },
  ];
};

const fetchTrustpilotData = async (): Promise<{
  reviewCount: number | null;
  rating: number | null;
  reviews: Review[];
}> => {
  const fallbackReviews = generateFallbackReviews();

  const { data, error } = await supabase
    .from('trustpilot_cache')
    .select('rating, review_count, reviews')
    .order('fetched_at', { ascending: false })
    .limit(1)
    .single();

  if (error || !data) {
    console.error('Error reading Trustpilot cache:', error);
    // No aggregate is better than a wrong one: callers render a placeholder instead.
    return { reviewCount: null, rating: null, reviews: fallbackReviews };
  }

  const serverReviews = Array.isArray(data.reviews) && data.reviews.length > 0
    ? (data.reviews as unknown as Review[])
    : fallbackReviews;
  const smartSorted = smartSortReviews(serverReviews.slice(0, 20));

  return {
    // Fall back to what we can actually count, never to a hard-coded figure.
    reviewCount: data.review_count ?? serverReviews.length ?? null,
    rating: data.rating ?? null,
    reviews: smartSorted,
  };
};

export const useTrustpilotStats = (): TrustpilotStats => {
  const { data, isLoading } = useQuery({
    queryKey: ['trustpilot-stats'],
    queryFn: fetchTrustpilotData,
    staleTime: 30 * 60 * 1000, // 30 min, matches cron refresh
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  return {
    reviewCount: data?.reviewCount ?? null,
    rating: data?.rating ?? null,
    reviews: data?.reviews ?? [],
    loading: isLoading,
  };
};
